import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  getHubspotConnectionSettings,
  updateHubspotConnectionSettings,
} from '../api/connection';
import type { HubspotConnectionSettings } from '../types';

interface UseHubspotConnectionOptions {
  enabled?: boolean;
}

export function useHubspotConnection(
  orgId: number | string | undefined,
  options?: UseHubspotConnectionOptions
) {
  const queryClient = useQueryClient();

  // The read path issues a PATCH with an empty body (no-op on the backend).
  // Disable ALL automatic refetch to prevent repeated PATCH calls.
  const settingsQuery = useQuery<HubspotConnectionSettings>({
    queryKey: ['hubspotConnection', orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return getHubspotConnectionSettings(orgId);
    },
    enabled: !!orgId && (options?.enabled ?? true),
    staleTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const updateSettings = useMutation({
    mutationFn: (body: Partial<HubspotConnectionSettings>) => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return updateHubspotConnectionSettings(orgId, body);
    },
    onSuccess: (data: HubspotConnectionSettings) =>
      queryClient.setQueryData(['hubspotConnection', orgId], data),
  });

  return useMemo(
    () => ({ settings: settingsQuery, updateSettings }),
    [settingsQuery, updateSettings]
  );
}
